import 'react-json-view-lite/dist/index.css';

import { useMemo } from 'react';
import { defaultStyles, JsonView as JsonTree } from 'react-json-view-lite';

interface JsonViewProps {
  value: unknown;
  label?: string;
  /** How many levels to auto-expand. Top `defaultExpandDepth` levels are open. */
  defaultExpandDepth?: number;
}

/** Recursively sort object keys alphabetically so the tree renders in a stable, scannable order.
 * Array order is preserved (it's usually meaningful); primitives pass through untouched. */
function sortKeysDeep(input: unknown): unknown {
  if (Array.isArray(input)) return input.map(sortKeysDeep);
  if (input !== null && typeof input === 'object') {
    const source = input as Record<string, unknown>;
    return Object.keys(source)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = sortKeysDeep(source[key]);
        return acc;
      }, {});
  }
  return input;
}

/** Tree styling for react-json-view-lite, mapped onto this dashboard's own Tailwind tokens
 * (mirrors really-data-ui's JsonView, axetay/really packages/really-data/ui). */
const treeStyles = {
  ...defaultStyles,
  container: 'whitespace-pre-wrap break-words',
  label: 'text-foreground font-semibold mr-1',
  clickableLabel: 'text-foreground font-semibold mr-1 cursor-pointer',
  expandIcon: `${defaultStyles.expandIcon} !text-muted-foreground`,
  collapseIcon: `${defaultStyles.collapseIcon} !text-muted-foreground`,
  punctuation: 'text-muted-foreground',
  nullValue: 'text-muted-foreground',
  undefinedValue: 'text-muted-foreground',
  stringValue: 'text-chart-1',
  numberValue: 'text-chart-2',
  booleanValue: 'text-primary',
  otherValue: 'text-chart-3',
};

export function JsonView({ value, label = 'JSON', defaultExpandDepth = 2 }: JsonViewProps) {
  const sorted = useMemo(() => sortKeysDeep(value), [value]);

  // The tree renderer only accepts objects and arrays. Primitives (and null/undefined) fall back
  // to a plain stringified block.
  const isTreeRenderable = typeof sorted === 'object' && sorted !== null;

  return (
    <div
      role="region"
      aria-label={label}
      tabIndex={0}
      className="max-h-96 overflow-auto rounded-lg bg-muted p-3 font-mono text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-ring"
    >
      {isTreeRenderable ? (
        <JsonTree data={sorted as object} style={treeStyles} shouldExpandNode={(level) => level < defaultExpandDepth} />
      ) : (
        <pre className="whitespace-pre-wrap break-words">{JSON.stringify(value, null, 2)}</pre>
      )}
    </div>
  );
}
